import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CreditCard, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * /billing — mints a fresh Stripe billing portal session and sends the member there.
 *
 * The reason this is a route and not a button: Stripe portal sessions expire in
 * minutes, so a link we generate ahead of time is dead by the time anyone clicks
 * it. A stable URL that creates the session on load is the only thing that can be
 * put in an email, a support reply, or a dunning notice.
 *
 * Members whose card expires previously had no way to fix it — the only "Manage
 * billing" control lived in a component nothing rendered — so they were dunned out
 * of a membership they wanted to keep. See docs/TICKET_billing_portal_entry_point.md.
 */
export default function BillingPage() {
  const navigate = useNavigate();
  const { getPortalUrl } = useSubscription();
  const [error, setError] = useState<string | null>(null);
  // React StrictMode double-invokes effects in dev; without this the page would
  // mint two portal sessions on every load.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const url = await getPortalUrl();
        if (url) { window.location.replace(url); return; }
        setError('no-subscription');
      } catch {
        setError('failed');
      }
    })();
  }, [getPortalUrl]);

  if (!error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3" style={{ background: 'var(--hj-paper)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
        <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink)' }}>Opening your billing page…</p>
      </div>
    );
  }

  // No Stripe customer: comped members, admin-granted accounts, and anyone who has
  // never subscribed all land here. Explain it — never show them a billing error.
  const noSub = error === 'no-subscription';
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-paper)', padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <CreditCard className="w-8 h-8" style={{ color: 'var(--hj-ink-3)', margin: '0 auto 14px' }} />
        <h1 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)', fontWeight: 800, letterSpacing: '-0.02em' }}>
          {noSub ? 'No billing to manage' : 'We could not open your billing page'}
        </h1>
        <p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.55, color: 'var(--hj-ink)' }}>
          {noSub
            ? 'There is no payment method on this account, so there is nothing to update. If your membership was granted to you, it is not billed and nothing is charged.'
            : 'Something went wrong on our end. Try again in a moment, and if it keeps happening tell us and we will sort it out.'}
        </p>
        <div className="flex flex-wrap gap-2.5 justify-center" style={{ marginTop: 20 }}>
          <Button onClick={() => navigate('/account')} className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to account
          </Button>
          {!noSub && (
            <Button variant="outline" asChild>
              <a href="mailto:hello@heyjoe.io?subject=Billing%20help">Email us</a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
